import { get } from "lodash";
import { RecordDataSource } from "../../../records/grids";
import { ColDef } from "../../../records/grids/columns/colDef";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetService,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  RecordGridFilterService,
  vsServiceName as RecordGridFilterServiceName,
} from "../../../records/grids/columns/filters/FilterProvider";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import { ContractNumberTypeFilter } from "./filters/contractNumbersFilter";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import { ValueGetter } from "../../../records/grids/cells/valueGetters";
import { vsLookupManagementServiceName as ContractLookupManagementServiceName } from "../lookupManagement";

export const vsObjectName = "contractInstance";
export const defaultSort = "ContractNumber";

contractInstanceConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];
export function contractInstanceConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  configureColumnDefinitionSet(columnDefinitionSetServiceProvider);
  configureGrid(recordGridViewServiceProvider);
}

function configureColumnDefinitionSet(
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    ContractLookupManagementServiceName,
    ServerUtilityName,
    RecordGridFilterServiceName,
    (
      columnDefinitionService: ColumnDefinitionService,
      contractLookupManagementService: LookupManagementService,
      serverUtility: ServerUtility,
      recordGridFilterService: RecordGridFilterService
    ): ColDef[] => {
      return [
        columnDefinitionService.getStringColDef({
          field: "ContractInstanceType",
          header: "Type",
        }),
        columnDefinitionService.getStringColDef({
          field: "ContractPropertyName",
          header: "Property",
        }),
        columnDefinitionService.getStringColDef({
          field: "ContractMachineManufacturerName",
          header: "Manufacturer",
          suppressSorting: true,
        }),
        columnDefinitionService.getStringColDef({
          field: "ContractNumber",
        }),
        getContractNumberColumn(
          contractLookupManagementService,
          serverUtility,
          recordGridFilterService,
          columnDefinitionService
        ),
        columnDefinitionService.getDateColDef({
          field: "ContractStartDate",
          header: "Contract Start",
        }),
        columnDefinitionService.getDateColDef({
          field: "ContractEndDate",
          header: "Contract End",
        }),
        columnDefinitionService.getStringColDef({
          field: "ContractInstanceNotes",
          header: "Notes",
          hide: true,
        }),
        columnDefinitionService.getStringColDef({
          field: "ContractStatusName",
          header: "Status",
        }),
        columnDefinitionService.getStringColDef({
          field: "ExecutedContract",
          header: "Executed Contract",
          hide: true,
          requiresPermission: "contractSensitiveInfo",
          suppressMenu: true,
          suppressSorting: true,
          valueGetter: (params) => get(params.data, "ExecutedContract.Name"),
          cellRenderer: (params) => {
            const executedContract = get(params.data, "ExecutedContract");
            if (!executedContract) {
              return "";
            }

            return `<a href="${executedContract.Url}" target="_blank">${executedContract.Name}</a>`;
          },
        }),
      ];
    },
  ]);
}

function getContractNumberColumn(
  contractLookupManagementService: LookupManagementService,
  serverUtility: ServerUtility,
  recordGridFilterService: RecordGridFilterService,
  columnDefinitionService: ColumnDefinitionService
): ColDef {
  const filter = recordGridFilterService
    .getFilterProvider("ContractNumberNumber")
    .addFilter(
      "By Type",
      new ContractNumberTypeFilter(
        contractLookupManagementService,
        serverUtility
      )
    )
    .defaultStringFilters()
    .getGridFilterFactory();

  const valueGetter: ValueGetter = (params) => {
    const contractNumberViews: any = (<any>params.data)?.ContractNumbers ?? [];
    const contractNumberStrings = contractNumberViews.map(
      (cn: any) => `${cn.ContractNumberTypeName}: ${cn.Number}`
    );
    return contractNumberStrings.join(", ");
  };

  const colDef = columnDefinitionService.simpleColDef(
    {
      field: "ContractNumbers",
      header: "Other Contract Numbers",
      hide: true,
      suppressSorting: true,
    },
    filter,
    valueGetter
  );

  return colDef;
}

function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ColumnDefinitionServiceName,
    ServerUtilityName,
    ColumnDefinitionSetServiceName,
    (
      columnDefinitionService: ColumnDefinitionService,
      serverUtility: ServerUtility,
      columnDefinitionSetService: ColumnDefinitionSetService
    ): GridConfiguration => {
      return {
        defaultSort: defaultSort,
        namePlural: "Contracts",
        nameSingular: "Contract",
        gridDataSource: new RecordDataSource(
          serverUtility,
          "/api/contract/contractInstance"
        ),
        columns: [
          columnDefinitionService.getNumberColDef({
            field: "Id",
            header: "Id",
          }),
          ...columnDefinitionSetService.getColumnDefinitionSet(vsObjectName),
        ],
      };
    },
  ]);
}
